/**
 * Life line construction algorithm.
 *
 * No React, no Konva, no rendering code.
 *
 * Main entry point:
 *   calculateLifeLine(designPoints, options)
 */

export type VertexCode =
  | "T"
  | "I"
  | "RC"
  | "FC"
  | `T${number}`;

export type Point = {
  x: number;
  y: number;
};

export type DiameterKind = "calculated" | "standard" | "terminal";

export type VertexKind =
  | "primary"     // original first-level design vertex
  | "chamfer"     // construction vertex created by acute-angle chamfer
  | "generated";  // generated by max-span subdivision

export type ChamferSide = "in" | "out";

export type LifeLineOptions = {
  /**
   * True when the polyline is closed.
   * The first/last point is represented once.
   */
  closed: boolean;

  /**
   * Maximum span length.
   * Segments longer than this are divided with generated "I" vertices.
   */
  systemMaxSpan: number;

  /**
   * Enable AutoCAD-like chamfer on first-level vertices with angle < 90°.
   * The loop connection vertex, sourcePrimaryIndex 0 in a closed line, is never chamfered.
   * When a closed loop has acute first-level vertices, the first acute vertex
   * in drawing order is used as the loop connection.
   */
  chamferAcuteAngles?: boolean;

  /**
   * Length of the new chamfer segment itself.
   * This is not the offset distance along the two original segments.
   */
  chamferSegmentLength?: number;

  /**
   * Maximum length for one created life line.
   * If total life-line length exceeds this value, the algorithm splits it into
   * multiple created lines using existing construction vertices.
   *
   * 0, undefined, null, or negative disables automatic splitting.
   */
  maxSingleLineLength?: number;

  /**
   * Maximum number of curve vertices allowed on one created life line.
   * This split rule is evaluated before maxSingleLineLength.
   *
   * 0, undefined, null, or negative disables curve-count splitting.
   */
  maxCornersPerLifeline?: number;
};

export type LifeLineSpan = {
  p1: Point;
  p2: Point;
  length: number;

  /**
   * Index of this construction segment before max-span subdivision.
   */
  segmentIndex: number;

  /**
   * Local span index inside the construction segment.
   */
  localIndex: number;

  fromConstructionIndex: number;
  toConstructionIndex: number;
};

export type ConstructionNode = Point & {
  /**
   * "primary" = original design point.
   * "chamfer" = point created around an acute design point.
   */
  baseKind: "primary" | "chamfer";

  /**
   * Index in the final construction path.
   */
  constructionIndex: number;

  /**
   * Index of the original first-level design vertex that generated this node.
   */
  sourcePrimaryIndex: number;

  primaryIndex?: number;
  chamferSide?: ChamferSide;

  /**
   * Codes before possible split-terminal override.
   */
  codes: VertexCode[];

  /**
   * Angle used for the final code of this construction node.
   * Null when angle information must not be shown, for example T or loop T1/T1.
   */
  angle: number | null;

  /**
   * Original first-level angle before chamfer.
   * Used to choose split candidates.
   */
  rawAngle: number | null;

  diameterKind: DiameterKind;

  /**
   * Requested chamfer segment length when this is a chamfer node.
   */
  requestedChamferSegmentLength?: number;

  /**
   * Actual offset used along the original adjacent segments.
   * This may be clamped when the requested chamfer segment is too long.
   */
  actualChamferOffset?: number;

  /**
   * Debug label that identifies the design source.
   */
  sourceLabel: string;
};

export type LifeLineVertex = Point & {
  kind: VertexKind;

  /**
   * Continuous position number in the final life-line sequence.
   */
  sequence: number;

  /**
   * Label without automatic split-terminal override.
   */
  label: string;

  /**
   * Label with automatic split-terminal override applied.
   * Use this field for display/export.
   */
  displayLabel: string;

  codes: VertexCode[];
  displayCodes: VertexCode[];

  angle: number | null;
  rawAngle: number | null;

  diameterKind: DiameterKind;

  constructionIndex?: number;
  sourcePrimaryIndex?: number;
  primaryIndex?: number;
  chamferSide?: ChamferSide;

  requestedChamferSegmentLength?: number;
  actualChamferOffset?: number;

  parentSegmentIndex?: number;
  localIndex?: number;
  spanLength?: number;

  fromSourcePrimaryIndex?: number;
  toSourcePrimaryIndex?: number;
  fromConstructionIndex?: number;
  toConstructionIndex?: number;
};

export type SplitLinePart = {
  lineNumber: number;

  startConstructionIndex: number;
  endConstructionIndex: number;

  startDistance: number;
  endDistance: number;
  length: number;

  /**
   * Construction nodes that compose this created life line.
   * In a closed loop, the last part may include the first construction node again
   * only inside this path array, without duplicating it in constructionNodes.
   */
  path: ConstructionNode[];

  /**
   * True when this part closes back to the first construction node.
   */
  closesToLoopStart?: boolean;
};

export type SplitCandidate = {
  constructionIndex: number;
  position: number;
  nodeAngle: number | null;
  isAcuteSource: boolean;
  sourcePrimaryIndex: number;
};

export type SplitPlan = {
  splitConstructionIndices: number[];
  splitCandidates: SplitCandidate[];

  /**
   * Key = constructionIndex.
   * Value = terminal codes to apply at that construction vertex.
   *
   * Example:
   *   split vertex between line 1 and 2 -> ["T1", "T2"]
   *   closed-loop start/end with 3 created lines -> ["T1", "T3"]
   */
  terminalCodesByConstructionIndex: Record<number, VertexCode[]>;

  lineParts: SplitLinePart[];
  lineCount: number;

  maxSingleLineLength: number;
  longestCreatedLineLength: number;

  hasAcuteSplit: boolean;
  reason: string;
};

export type LifeLineResult = {
  designPoints: Point[];
  closed: boolean;

  options: Required<Pick<
    LifeLineOptions,
    | "closed"
    | "systemMaxSpan"
    | "chamferAcuteAngles"
    | "chamferSegmentLength"
    | "maxSingleLineLength"
    | "maxCornersPerLifeline"
  >>;

  /**
   * Geometry after first-level construction:
   * - original first-level vertices,
   * - chamfer vertices,
   * - but not max-span generated vertices.
   */
  constructionNodes: ConstructionNode[];

  /**
   * Final sequence:
   * - construction nodes,
   * - max-span generated vertices.
   */
  vertices: LifeLineVertex[];

  /**
   * Only max-span generated vertices.
   */
  generatedVertices: LifeLineVertex[];

  spans: LifeLineSpan[];

  totalLength: number;

  /**
   * Null when the maxSingleLineLength option is disabled or not exceeded.
   */
  splitPlan: SplitPlan | null;

  /**
   * Created life lines. If there is no split, this contains one line.
   */
  lines: SplitLinePart[];
};

export type RenderedLifeLineVertex = Pick<
  LifeLineVertex,
  "x" | "y" | "kind" | "label" | "displayLabel" | "codes" | "displayCodes"
>;

export type RenderedLifeLine = {
  lineNumber: number;
  line: SplitLinePart;
  points: number[];
  lifeLineVertices: RenderedLifeLineVertex[];
  /**
   * Editable first-level design vertices for this rendered line.
   * Chamfer and generated vertices remain render-only construction points.
   */
  designPoints: Point[];
};

const EPSILON = 0.001;

const DEFAULT_SYSTEM_MAX_SPAN = 130;
const DEFAULT_CHAMFER_SEGMENT_LENGTH = 20;
const DEFAULT_MAX_SINGLE_LINE_LENGTH = 0;
const CLOSED_LOOP_MULTI_ACUTE_SPLIT_RATIO = 0.75;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function cleanNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function sanitizePoint(point: Point): Point {
  return {
    x: cleanNumber(point.x, 0),
    y: cleanNumber(point.y, 0),
  };
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function pointAlong(a: Point, b: Point, t: number): Point {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
}

function arePointsEqual(pointA: Point, pointB: Point): boolean {
  return Math.abs(pointA.x - pointB.x) < 0.0001 && Math.abs(pointA.y - pointB.y) < 0.0001;
}

export function angleAtPoint(prev: Point, current: Point, next: Point): number | null {
  const v1 = {
    x: prev.x - current.x,
    y: prev.y - current.y,
  };

  const v2 = {
    x: next.x - current.x,
    y: next.y - current.y,
  };

  const len1 = Math.hypot(v1.x, v1.y);
  const len2 = Math.hypot(v2.x, v2.y);

  if (len1 <= EPSILON || len2 <= EPSILON) {
    return null;
  }

  const dot = v1.x * v2.x + v1.y * v2.y;
  const cosine = clamp(dot / (len1 * len2), -1, 1);

  return Math.acos(cosine) * 180 / Math.PI;
}

export function angleAtFirstLevelVertex(
  designPoints: Point[],
  index: number,
  closed: boolean,
): number | null {
  const n = designPoints.length;

  if (n < 3) {
    return null;
  }

  if (!closed && (index === 0 || index === n - 1)) {
    return null;
  }

  const prevIndex = index === 0 ? n - 1 : index - 1;
  const nextIndex = index === n - 1 ? 0 : index + 1;

  if (!closed && (prevIndex < 0 || nextIndex >= n)) {
    return null;
  }

  const prevPoint = designPoints[prevIndex];
  const currentPoint = designPoints[index];
  const nextPoint = designPoints[nextIndex];
  if (!prevPoint || !currentPoint || !nextPoint) return null;

  return angleAtPoint(prevPoint, currentPoint, nextPoint);
}

function rotateClosedLoopToAcuteStart(
  designPoints: Point[],
  closed: boolean,
): Point[] {
  if (!closed || designPoints.length < 3) {
    return designPoints;
  }

  const acuteIndices = designPoints
    .map((_, index) => ({
      index,
      angle: angleAtFirstLevelVertex(designPoints, index, true),
    }))
    .filter(({ angle }) => angle !== null && Number.isFinite(angle) && angle < 90)
    .map(({ index }) => index);

  if (acuteIndices.length === 0 || acuteIndices[0] === 0) {
    return designPoints;
  }

  const acuteIndex = acuteIndices[0];
  return [
    ...designPoints.slice(acuteIndex),
    ...designPoints.slice(0, acuteIndex),
  ];
}

export function getAngleCode(angle: number | null): VertexCode | null {
  if (angle === null || !Number.isFinite(angle)) {
    return null;
  }

  if (angle >= 165 && angle <= 180) {
    return "I";
  }

  if (angle >= 90 && angle <= 95) {
    return "RC";
  }

  if (angle > 95 && angle < 165) {
    return "FC";
  }

  return null;
}

/**
 * Chamfer vertices are always corner vertices.
 * So after a chamfer, the new created corners become RC or FC.
 * They do not become I when their constructed angle is near 180°.
 */
export function getChamferCornerCode(angle: number | null): VertexCode | null {
  if (angle === null || !Number.isFinite(angle)) {
    return null;
  }

  if (angle >= 90 && angle <= 95) {
    return "RC";
  }

  if (angle > 95 && angle <= 180) {
    return "FC";
  }

  return null;
}

function makeLabel(sequence: number, codes: VertexCode[]): string {
  return codes.length ? `${sequence}${codes.join("/")}` : String(sequence);
}

function normalizeOptions(options: LifeLineOptions): Required<Pick<
  LifeLineOptions,
  | "closed"
  | "systemMaxSpan"
  | "chamferAcuteAngles"
  | "chamferSegmentLength"
  | "maxSingleLineLength"
  | "maxCornersPerLifeline"
>> {
  const systemMaxSpan = Math.max(
    1,
    cleanNumber(options.systemMaxSpan, DEFAULT_SYSTEM_MAX_SPAN),
  );

  const chamferSegmentLength = Math.max(
    0,
    cleanNumber(options.chamferSegmentLength, DEFAULT_CHAMFER_SEGMENT_LENGTH),
  );

  const maxSingleLineLength = Math.max(
    0,
    cleanNumber(options.maxSingleLineLength, DEFAULT_MAX_SINGLE_LINE_LENGTH),
  );
  const maxCornersPerLifeline = Math.floor(
    Math.max(0, cleanNumber(options.maxCornersPerLifeline, 0)),
  );

  return {
    closed: Boolean(options.closed),
    systemMaxSpan,
    chamferAcuteAngles: options.chamferAcuteAngles ?? true,
    chamferSegmentLength,
    maxSingleLineLength,
    maxCornersPerLifeline,
  };
}

function pointFromVertexToward(vertex: Point, target: Point, offset: number): Point {
  const len = distance(vertex, target);

  if (len <= EPSILON) {
    return { ...vertex };
  }

  const d = Math.min(offset, len);

  return {
    x: vertex.x + ((target.x - vertex.x) / len) * d,
    y: vertex.y + ((target.y - vertex.y) / len) * d,
  };
}

function angleAtConstructionNode(
  constructionNodes: ConstructionNode[],
  index: number,
  closed: boolean,
): number | null {
  const n = constructionNodes.length;

  if (n < 3) {
    return null;
  }

  if (!closed && (index === 0 || index === n - 1)) {
    return null;
  }

  const prevIndex = index === 0 ? n - 1 : index - 1;
  const nextIndex = index === n - 1 ? 0 : index + 1;

  if (!closed && (prevIndex < 0 || nextIndex >= n)) {
    return null;
  }

  const prevNode = constructionNodes[prevIndex];
  const currentNode = constructionNodes[index];
  const nextNode = constructionNodes[nextIndex];
  if (!prevNode || !currentNode || !nextNode) return null;

  return angleAtPoint(prevNode, currentNode, nextNode);
}

function isLoopConnectionSource(
  closed: boolean,
  sourcePrimaryIndex: number,
  designPointCount: number,
): boolean {
  return closed && designPointCount > 2 && sourcePrimaryIndex === 0;
}

function shouldChamferVertex(params: {
  designPoints: Point[];
  index: number;
  closed: boolean;
  chamferAcuteAngles: boolean;
  chamferSegmentLength: number;
  forcedPrimaryIndices: number[];
}): boolean {
  const {
    designPoints,
    index,
    closed,
    chamferAcuteAngles,
    chamferSegmentLength,
    forcedPrimaryIndices,
  } = params;

  const n = designPoints.length;

  if (!chamferAcuteAngles) {
    return false;
  }

  if (chamferSegmentLength <= 0) {
    return false;
  }

  if (n < 3) {
    return false;
  }

  if (!closed && (index === 0 || index === n - 1)) {
    return false;
  }

  /**
   * The loop connection remains unchanged.
   * Even if its angle is acute, it is not chamfered.
   */
  if (isLoopConnectionSource(closed, index, n)) {
    return false;
  }

  /**
   * When an acute first-level design vertex is selected as split terminal,
   * it returns to being a unique vertex with Tn/Tn+1.
   */
  if (forcedPrimaryIndices.includes(index)) {
    return false;
  }

  const angle = angleAtFirstLevelVertex(designPoints, index, closed);

  return angle !== null && angle < 90;
}

function chamferOffsetFromNewSegmentLength(
  designPoints: Point[],
  index: number,
  closed: boolean,
  desiredNewSegmentLength: number,
): number {
  const n = designPoints.length;
  const prevIndex = index === 0 ? n - 1 : index - 1;
  const nextIndex = index === n - 1 ? 0 : index + 1;

  const vertex = designPoints[index];
  const prev = designPoints[prevIndex];
  const next = designPoints[nextIndex];
  if (!vertex || !prev || !next) return 0;

  const angle = angleAtFirstLevelVertex(designPoints, index, closed);

  if (angle === null || angle <= EPSILON) {
    return 0;
  }

  const angleRad = angle * Math.PI / 180;
  const sinHalf = Math.sin(angleRad / 2);

  if (sinHalf <= EPSILON) {
    return 0;
  }

  /**
   * Input is the length of the new chamfer segment.
   * For equal offsets on the two adjacent original segments:
   *
   *   newSegmentLength = 2 * offset * sin(angle / 2)
   *
   * Therefore:
   *
   *   offset = newSegmentLength / (2 * sin(angle / 2))
   */
  const requestedOffset = desiredNewSegmentLength / (2 * sinHalf);

  const prevLength = distance(vertex, prev);
  const nextLength = distance(vertex, next);

  /**
   * Safety clamp: do not consume an entire adjacent segment.
   */
  return Math.max(0, Math.min(
    requestedOffset,
    prevLength * 0.45,
    nextLength * 0.45,
  ));
}

function makePrimaryNode(
  designPoints: Point[],
  index: number,
  closed: boolean,
): ConstructionNode {
  const point = designPoints[index];
  if (!point) {
    throw new Error(`Invalid design point index ${index}`);
  }

  return {
    x: point.x,
    y: point.y,
    baseKind: "primary",
    constructionIndex: -1,
    sourcePrimaryIndex: index,
    primaryIndex: index,
    codes: [],
    angle: null,
    rawAngle: angleAtFirstLevelVertex(designPoints, index, closed),
    diameterKind: "standard",
    sourceLabel: `P${index + 1}`,
  };
}

function makeChamferNodes(
  designPoints: Point[],
  index: number,
  closed: boolean,
  desiredNewSegmentLength: number,
): [ConstructionNode, ConstructionNode] {
  const n = designPoints.length;
  const vertex = designPoints[index];
  const prevIndex = index === 0 ? n - 1 : index - 1;
  const nextIndex = index === n - 1 ? 0 : index + 1;
  const prevPoint = designPoints[prevIndex];
  const nextPoint = designPoints[nextIndex];
  if (!vertex || !prevPoint || !nextPoint) {
    throw new Error(`Invalid design point index ${index}`);
  }

  const offset = chamferOffsetFromNewSegmentLength(
    designPoints,
    index,
    closed,
    desiredNewSegmentLength,
  );

  const rawAngle = angleAtFirstLevelVertex(designPoints, index, closed);

  const inPoint = pointFromVertexToward(vertex, prevPoint, offset);
  const outPoint = pointFromVertexToward(vertex, nextPoint, offset);

  const inNode: ConstructionNode = {
    ...inPoint,
    baseKind: "chamfer",
    constructionIndex: -1,
    chamferSide: "in",
    sourcePrimaryIndex: index,
    codes: [],
    angle: null,
    rawAngle,
    diameterKind: "standard",
    requestedChamferSegmentLength: desiredNewSegmentLength,
    actualChamferOffset: offset,
    sourceLabel: `CH${index + 1}A`,
  };

  const outNode: ConstructionNode = {
    ...outPoint,
    baseKind: "chamfer",
    constructionIndex: -1,
    chamferSide: "out",
    sourcePrimaryIndex: index,
    codes: [],
    angle: null,
    rawAngle,
    diameterKind: "standard",
    requestedChamferSegmentLength: desiredNewSegmentLength,
    actualChamferOffset: offset,
    sourceLabel: `CH${index + 1}B`,
  };

  return [inNode, outNode];
}

function classifyConstructionNodes(
  unclassifiedNodes: ConstructionNode[],
  designPoints: Point[],
  closed: boolean,
): ConstructionNode[] {
  const designPointCount = designPoints.length;

  const nodes = unclassifiedNodes.map((node, index) => ({
    ...node,
    constructionIndex: index,
  }));

  return nodes.map((node, index) => {
    const next: ConstructionNode = { ...node };

    const isOpenTerminal =
      !closed &&
      node.baseKind === "primary" &&
      designPointCount > 1 &&
      (
        node.sourcePrimaryIndex === 0 ||
        node.sourcePrimaryIndex === designPointCount - 1
      );

    const isLoopConnection =
      isLoopConnectionSource(closed, node.sourcePrimaryIndex, designPointCount) &&
      node.baseKind === "primary";

    /**
     * Final rule:
     * - A closed loop made by one single created life line uses T1/T1.
     * - We do not display L anymore.
     * - No angle information is shown at the loop connection.
     *
     * If the loop is split into more created lines, split labels will override
     * this T1/T1 into T1/Tn.
     */
    if (isLoopConnection) {
      next.codes = ["T1", "T1"];
      next.angle = null;
      next.rawAngle = null;
      next.diameterKind = "standard";
      return next;
    }

    if (isOpenTerminal) {
      next.codes = ["T"];
      next.angle = null;
      next.rawAngle = null;
      next.diameterKind = "terminal";
      return next;
    }

    const constructedAngle = angleAtConstructionNode(nodes, index, closed);

    if (node.baseKind === "chamfer") {
      const code = getChamferCornerCode(constructedAngle);

      next.codes = code ? [code] : [];
      next.angle = constructedAngle;
      next.diameterKind = "standard";

      return next;
    }

    const code = getAngleCode(constructedAngle);

    next.codes = code ? [code] : [];
    next.angle = constructedAngle;
    next.diameterKind =
      constructedAngle !== null && constructedAngle < 90
        ? "calculated"
        : "standard";

    return next;
  });
}

function buildConstructionNodes(params: {
  designPoints: Point[];
  closed: boolean;
  chamferAcuteAngles: boolean;
  chamferSegmentLength: number;
  forcedPrimaryIndices: number[];
}): ConstructionNode[] {
  const {
    designPoints,
    closed,
    chamferAcuteAngles,
    chamferSegmentLength,
    forcedPrimaryIndices,
  } = params;

  const nodes: ConstructionNode[] = [];

  for (let i = 0; i < designPoints.length; i += 1) {
    const useChamfer = shouldChamferVertex({
      designPoints,
      index: i,
      closed,
      chamferAcuteAngles,
      chamferSegmentLength,
      forcedPrimaryIndices,
    });

    if (useChamfer) {
      const [inNode, outNode] = makeChamferNodes(
        designPoints,
        i,
        closed,
        chamferSegmentLength,
      );

      nodes.push(inNode, outNode);
    } else {
      nodes.push(makePrimaryNode(designPoints, i, closed));
    }
  }

  return classifyConstructionNodes(nodes, designPoints, closed);
}

type InternalBuildResult = {
  constructionNodes: ConstructionNode[];
  vertices: LifeLineVertex[];
  generatedVertices: LifeLineVertex[];
  spans: LifeLineSpan[];
  totalLength: number;
};

function buildLifeLineOnce(params: {
  designPoints: Point[];
  closed: boolean;
  systemMaxSpan: number;
  chamferAcuteAngles: boolean;
  chamferSegmentLength: number;
  forcedPrimaryIndices: number[];
}): InternalBuildResult {
  const {
    designPoints,
    closed,
    systemMaxSpan,
    chamferAcuteAngles,
    chamferSegmentLength,
    forcedPrimaryIndices,
  } = params;

  const constructionNodes = buildConstructionNodes({
    designPoints,
    closed,
    chamferAcuteAngles,
    chamferSegmentLength,
    forcedPrimaryIndices,
  });

  const vertices: LifeLineVertex[] = [];
  const generatedVertices: LifeLineVertex[] = [];
  const spans: LifeLineSpan[] = [];

  let sequence = 1;
  let totalLength = 0;
  let segmentIndex = 0;

  const addConstructionVertex = (node: ConstructionNode): void => {
    const vertex: LifeLineVertex = {
      x: node.x,
      y: node.y,
      kind: node.baseKind === "primary" ? "primary" : "chamfer",
      sequence,
      codes: [...node.codes],
      displayCodes: [...node.codes],
      label: makeLabel(sequence, node.codes),
      displayLabel: makeLabel(sequence, node.codes),
      angle: node.angle,
      rawAngle: node.rawAngle,
      diameterKind: node.diameterKind,
      constructionIndex: node.constructionIndex,
      sourcePrimaryIndex: node.sourcePrimaryIndex,
      primaryIndex: node.primaryIndex,
      chamferSide: node.chamferSide,
      requestedChamferSegmentLength: node.requestedChamferSegmentLength,
      actualChamferOffset: node.actualChamferOffset,
    };

    vertices.push(vertex);
    sequence += 1;
  };

  const addGeneratedVertex = (
    point: Point,
    localIndex: number,
    spanLength: number,
    fromNode: ConstructionNode,
    toNode: ConstructionNode,
  ): void => {
    const codes: VertexCode[] = ["I"];

    const vertex: LifeLineVertex = {
      x: point.x,
      y: point.y,
      kind: "generated",
      sequence,
      codes,
      displayCodes: [...codes],
      label: makeLabel(sequence, codes),
      displayLabel: makeLabel(sequence, codes),
      angle: null,
      rawAngle: null,
      diameterKind: "calculated",
      parentSegmentIndex: segmentIndex,
      localIndex,
      spanLength,
      fromSourcePrimaryIndex: fromNode.sourcePrimaryIndex,
      toSourcePrimaryIndex: toNode.sourcePrimaryIndex,
      fromConstructionIndex: fromNode.constructionIndex,
      toConstructionIndex: toNode.constructionIndex,
    };

    vertices.push(vertex);
    generatedVertices.push(vertex);
    sequence += 1;
  };

  const addSegment = (
    fromNode: ConstructionNode,
    toNode: ConstructionNode,
    addEndNode: boolean,
  ): void => {
    const length = distance(fromNode, toNode);
    totalLength += length;

    if (length <= EPSILON) {
      if (addEndNode) {
        addConstructionVertex(toNode);
      }

      segmentIndex += 1;
      return;
    }

    const parts = Math.max(1, Math.ceil(length / systemMaxSpan));
    const spanLength = length / parts;

    for (let k = 0; k < parts; k += 1) {
      const p1 = pointAlong(fromNode, toNode, k / parts);
      const p2 = pointAlong(fromNode, toNode, (k + 1) / parts);

      spans.push({
        p1,
        p2,
        length: distance(p1, p2),
        segmentIndex,
        localIndex: k,
        fromConstructionIndex: fromNode.constructionIndex,
        toConstructionIndex: toNode.constructionIndex,
      });
    }

    for (let k = 1; k < parts; k += 1) {
      addGeneratedVertex(
        pointAlong(fromNode, toNode, k / parts),
        k,
        spanLength,
        fromNode,
        toNode,
      );
    }

    if (addEndNode) {
      addConstructionVertex(toNode);
    }

    segmentIndex += 1;
  };

  if (constructionNodes.length === 0) {
    return {
      constructionNodes,
      vertices,
      generatedVertices,
      spans,
      totalLength,
    };
  }

  const firstConstructionNode = constructionNodes[0];
  if (!firstConstructionNode) {
    return {
      constructionNodes,
      vertices,
      generatedVertices,
      spans,
      totalLength,
    };
  }
  addConstructionVertex(firstConstructionNode);

  for (let i = 0; i < constructionNodes.length - 1; i += 1) {
    const current = constructionNodes[i];
    const nextNode = constructionNodes[i + 1];
    if (!current || !nextNode) continue;
    addSegment(current, nextNode, true);
  }

  if (closed && constructionNodes.length > 1) {
    /**
     * Closed path: add the closing segment without duplicating the first vertex.
     */
    const lastNode = constructionNodes[constructionNodes.length - 1];
    if (lastNode) {
      addSegment(lastNode, firstConstructionNode, false);
    }
  }

  return {
    constructionNodes,
    vertices,
    generatedVertices,
    spans,
    totalLength,
  };
}

function pathPositionsForConstructionNodes(
  constructionNodes: ConstructionNode[],
  closed: boolean,
): {
  positions: number[];
  total: number;
  closingLength: number;
} {
  const positions: number[] = [];
  let cursor = 0;

  if (constructionNodes.length === 0) {
    return {
      positions,
      total: 0,
      closingLength: 0,
    };
  }

  positions[0] = 0;

  for (let i = 1; i < constructionNodes.length; i += 1) {
    const previous = constructionNodes[i - 1];
    const current = constructionNodes[i];
    if (!previous || !current) continue;
    cursor += distance(previous, current);
    positions[i] = cursor;
  }

  const lastConstructionNode = constructionNodes[constructionNodes.length - 1];
  const firstConstructionNode = constructionNodes[0];
  const closingLength =
    closed && constructionNodes.length > 1 && lastConstructionNode && firstConstructionNode
      ? distance(lastConstructionNode, firstConstructionNode)
      : 0;

  return {
    positions,
    total: cursor + closingLength,
    closingLength,
  };
}

function isLoopConnectionNode(node: ConstructionNode, closed: boolean): boolean {
  return closed && node.baseKind === "primary" && node.sourcePrimaryIndex === 0;
}

type SplitSearchInterval = {
  startIndex: number;
  endIndex: number;
  startDistance: number;
  endDistance: number;
  closesToLoopStart?: boolean;
};

type InternalSplitCandidate = SplitCandidate & {
  node: ConstructionNode;
  isOpenEndpoint: boolean;
  isLoopConnection: boolean;
};

function isAcuteSourceCandidate(candidate: InternalSplitCandidate): boolean {
  return (
    candidate.node.rawAngle !== null &&
    Number.isFinite(candidate.node.rawAngle) &&
    candidate.node.rawAngle < 90 &&
    !candidate.isLoopConnection
  );
}

function hasClosedLoopAcuteSplitCandidate(
  constructionNodes: ConstructionNode[],
  closed: boolean,
): boolean {
  if (!closed) {
    return false;
  }

  const acuteSourcePrimaryIndices = new Set<number>();

  constructionNodes.forEach((node) => {
    if (
      node.rawAngle !== null &&
      Number.isFinite(node.rawAngle) &&
      node.rawAngle < 90 &&
      !isLoopConnectionNode(node, closed)
    ) {
      acuteSourcePrimaryIndices.add(node.sourcePrimaryIndex);
    }
  });

  return acuteSourcePrimaryIndices.size > 0;
}

function isCurveConstructionNode(node: ConstructionNode): boolean {
  return node.codes.some((code) => code === "RC" || code === "FC");
}

function makeInternalSplitCandidate(params: {
  constructionNodes: ConstructionNode[];
  closed: boolean;
  pathPositions: number[];
  index: number;
}): InternalSplitCandidate {
  const {
    constructionNodes,
    closed,
    pathPositions,
    index,
  } = params;
  const node = constructionNodes[index];
  if (!node) {
    throw new Error(`Invalid construction node index ${index}`);
  }
  const position = pathPositions[index] ?? 0;
  const isOpenEndpoint =
    !closed && (index === 0 || index === constructionNodes.length - 1);
  const isLoopConnection = isLoopConnectionNode(node, closed);
  const nodeAngle = angleAtConstructionNode(constructionNodes, index, closed);
  const isAcuteSource =
    node.rawAngle !== null &&
    Number.isFinite(node.rawAngle) &&
    node.rawAngle < 90 &&
    !isLoopConnection;

  return {
    constructionIndex: index,
    position,
    nodeAngle,
    isAcuteSource,
    sourcePrimaryIndex: node.sourcePrimaryIndex,
    node,
    isOpenEndpoint,
    isLoopConnection,
  };
}

function findCornerLimitSplitCandidates(params: {
  constructionNodes: ConstructionNode[];
  closed: boolean;
  pathPositions: number[];
  maxCornersPerLifeline: number;
}): InternalSplitCandidate[] {
  const {
    constructionNodes,
    closed,
    pathPositions,
    maxCornersPerLifeline,
  } = params;

  if (!Number.isFinite(maxCornersPerLifeline) || maxCornersPerLifeline <= 0) {
    return [];
  }

  if (constructionNodes.length < 3) {
    return [];
  }

  const splitCandidates: InternalSplitCandidate[] = [];
  let curveCount = 0;
  const lastOpenIndex = constructionNodes.length - 1;

  for (let index = 0; index < constructionNodes.length; index += 1) {
    const node = constructionNodes[index];
    if (!node) continue;
    if (isLoopConnectionNode(node, closed)) continue;
    if (!closed && (index === 0 || index === lastOpenIndex)) continue;
    if (!isCurveConstructionNode(node)) continue;

    curveCount += 1;

    if (curveCount <= maxCornersPerLifeline) {
      continue;
    }

    const candidate = makeInternalSplitCandidate({
      constructionNodes,
      closed,
      pathPositions,
      index,
    });

    if (candidate.isOpenEndpoint || candidate.isLoopConnection) {
      continue;
    }

    splitCandidates.push(candidate);
    curveCount = 0;
  }

  return splitCandidates;
}

function chooseSplitCandidateForInterval(params: {
  constructionNodes: ConstructionNode[];
  closed: boolean;
  pathPositions: number[];
  interval: SplitSearchInterval;
}): InternalSplitCandidate | null {
  const {
    constructionNodes,
    closed,
    pathPositions,
    interval,
  } = params;

  const target = (interval.startDistance + interval.endDistance) / 2;

  const candidates: InternalSplitCandidate[] = constructionNodes
    .map((_, index) =>
      makeInternalSplitCandidate({
        constructionNodes,
        closed,
        pathPositions,
        index,
      }),
    )
    .filter((candidate) => {
      if (candidate.isOpenEndpoint) {
        return false;
      }

      if (candidate.isLoopConnection) {
        return false;
      }

      return (
        candidate.position > interval.startDistance + EPSILON &&
        candidate.position < interval.endDistance - EPSILON
      );
    });

  if (candidates.length === 0) {
    return null;
  }

  const acuteCandidates = candidates.filter(isAcuteSourceCandidate);

  const normalCandidates = candidates.filter((candidate) =>
    candidate.nodeAngle !== null &&
    Number.isFinite(candidate.nodeAngle) &&
    candidate.nodeAngle >= 90,
  );

  const pool =
    acuteCandidates.length > 0
      ? acuteCandidates
      : normalCandidates.length > 0
        ? normalCandidates
        : candidates;

  const initialCandidate = pool[0];
  if (!initialCandidate) return null;

  return pool.reduce((best, candidate) => {
    const score = Math.abs(candidate.position - target);
    const bestScore = Math.abs(best.position - target);

    return score < bestScore ? candidate : best;
  }, initialCandidate);
}

function pathForLinePart(
  constructionNodes: ConstructionNode[],
  part: Omit<SplitLinePart, "path" | "length">,
  closed: boolean,
): ConstructionNode[] {
  if (!closed) {
    return constructionNodes.slice(
      part.startConstructionIndex,
      part.endConstructionIndex + 1,
    );
  }

  if (
    part.closesToLoopStart ||
    (
      part.endConstructionIndex === 0 &&
      part.endDistance > part.startDistance
    )
  ) {
    const firstNode = constructionNodes[0];
    const tail = constructionNodes.slice(part.startConstructionIndex);
    return firstNode ? [...tail, firstNode] : tail;
  }

  return constructionNodes.slice(
    part.startConstructionIndex,
    part.endConstructionIndex + 1,
  );
}

function buildLinePartsFromSplitIndices(params: {
  constructionNodes: ConstructionNode[];
  closed: boolean;
  splitConstructionIndices: number[];
  pathPositions: number[];
  totalLength: number;
}): SplitLinePart[] {
  const {
    constructionNodes,
    closed,
    splitConstructionIndices,
    pathPositions,
    totalLength,
  } = params;

  const sorted = [...new Set(splitConstructionIndices)]
    .filter((index) =>
      Number.isInteger(index) &&
      index >= 0 &&
      index < constructionNodes.length,
    )
    .sort((a, b) => (pathPositions[a] ?? 0) - (pathPositions[b] ?? 0));

  if (sorted.length === 0) {
    const firstNode = constructionNodes[0];
    return [{
      lineNumber: 1,
      startConstructionIndex: 0,
      endConstructionIndex: closed ? 0 : constructionNodes.length - 1,
      startDistance: 0,
      endDistance: totalLength,
      length: totalLength,
      closesToLoopStart: closed,
      path: closed && firstNode
        ? [...constructionNodes, firstNode]
        : [...constructionNodes],
    }];
  }

  const partsWithoutPath: Array<Omit<SplitLinePart, "path" | "length">> = [];

  if (closed) {
    let startIndex = 0;
    let startDistance = 0;

    sorted.forEach((endIndex, i) => {
      const endDistance = pathPositions[endIndex] ?? 0;

      partsWithoutPath.push({
        lineNumber: i + 1,
        startConstructionIndex: startIndex,
        endConstructionIndex: endIndex,
        startDistance,
        endDistance,
      });

      startIndex = endIndex;
      startDistance = endDistance;
    });

    partsWithoutPath.push({
      lineNumber: sorted.length + 1,
      startConstructionIndex: startIndex,
      endConstructionIndex: 0,
      startDistance,
      endDistance: totalLength,
      closesToLoopStart: true,
    });
  } else {
    const breakpoints = [
      0,
      ...sorted,
      constructionNodes.length - 1,
    ];

    for (let i = 0; i < breakpoints.length - 1; i += 1) {
      const startIndex = breakpoints[i];
      const endIndex = breakpoints[i + 1];
      if (startIndex === undefined || endIndex === undefined) continue;

      partsWithoutPath.push({
        lineNumber: i + 1,
        startConstructionIndex: startIndex,
        endConstructionIndex: endIndex,
        startDistance: pathPositions[startIndex] ?? 0,
        endDistance: pathPositions[endIndex] ?? 0,
      });
    }
  }

  return partsWithoutPath.map((part) => {
    const length = Math.max(0, part.endDistance - part.startDistance);

    return {
      ...part,
      length,
      path: pathForLinePart(constructionNodes, part, closed),
    };
  });
}

function buildTerminalCodesByConstructionIndex(params: {
  splitConstructionIndices: number[];
  lineParts: SplitLinePart[];
  closed: boolean;
}): Record<number, VertexCode[]> {
  const {
    splitConstructionIndices,
    lineParts,
    closed,
  } = params;

  const terminalCodesByConstructionIndex: Record<number, VertexCode[]> = {};

  if (lineParts.length <= 1) {
    return terminalCodesByConstructionIndex;
  }

  if (closed) {
    /**
     * In a split loop, the loop connection becomes:
     * - T1/T2 for two created lines,
     * - T1/T3 for three created lines,
     * - T1/Tn for n created lines.
     */
    terminalCodesByConstructionIndex[0] = [
      "T1",
      `T${lineParts.length}` as VertexCode,
    ];

    splitConstructionIndices.forEach((constructionIndex, i) => {
      terminalCodesByConstructionIndex[constructionIndex] = [
        `T${i + 1}` as VertexCode,
        `T${i + 2}` as VertexCode,
      ];
    });
  } else {
    splitConstructionIndices.forEach((constructionIndex, i) => {
      terminalCodesByConstructionIndex[constructionIndex] = [
        `T${i + 1}` as VertexCode,
        `T${i + 2}` as VertexCode,
      ];
    });
  }

  return terminalCodesByConstructionIndex;
}

function findSplitPlan(params: {
  constructionNodes: ConstructionNode[];
  closed: boolean;
  totalLength: number;
  maxSingleLineLength: number;
  maxCornersPerLifeline: number;
}): SplitPlan | null {
  const {
    constructionNodes,
    closed,
    totalLength,
    maxSingleLineLength,
    maxCornersPerLifeline,
  } = params;

  if (constructionNodes.length < 3) {
    return null;
  }

  const pathInfo = pathPositionsForConstructionNodes(constructionNodes, closed);
  const cornerLimitSplitCandidates = findCornerLimitSplitCandidates({
    constructionNodes,
    closed,
    pathPositions: pathInfo.positions,
    maxCornersPerLifeline,
  });
  const cornerLimitSplitConstructionIndices = cornerLimitSplitCandidates.map(
    (candidate) => candidate.constructionIndex,
  );
  const hasLengthSplitRule = Number.isFinite(maxSingleLineLength) && maxSingleLineLength > 0;

  if (!hasLengthSplitRule && cornerLimitSplitCandidates.length === 0) {
    return null;
  }

  const earlyClosedLoopAcuteSplitLength =
    maxSingleLineLength * CLOSED_LOOP_MULTI_ACUTE_SPLIT_RATIO;
  const useEarlyClosedLoopAcuteSplit =
    hasLengthSplitRule &&
    hasClosedLoopAcuteSplitCandidate(constructionNodes, closed) &&
    totalLength >= earlyClosedLoopAcuteSplitLength;
  const splitTriggerLength = useEarlyClosedLoopAcuteSplit
    ? earlyClosedLoopAcuteSplitLength
    : maxSingleLineLength;

  const splitCandidates: InternalSplitCandidate[] = [...cornerLimitSplitCandidates];

  const intervals: SplitSearchInterval[] = cornerLimitSplitConstructionIndices.length > 0
    ? buildLinePartsFromSplitIndices({
        constructionNodes,
        closed,
        splitConstructionIndices: cornerLimitSplitConstructionIndices,
        pathPositions: pathInfo.positions,
        totalLength,
      }).map((part) => ({
        startIndex: part.startConstructionIndex,
        endIndex: part.endConstructionIndex,
        startDistance: part.startDistance,
        endDistance: part.endDistance,
        closesToLoopStart: part.closesToLoopStart,
      }))
    : closed
      ? [{
          startIndex: 0,
          endIndex: 0,
          startDistance: 0,
          endDistance: pathInfo.total,
          closesToLoopStart: true,
        }]
      : [{
          startIndex: 0,
          endIndex: constructionNodes.length - 1,
          startDistance: 0,
          endDistance: pathInfo.total,
        }];

  /**
   * Prevent accidental infinite loops if the geometry cannot be split further.
   */
  const maxIterations = Math.min(
    50,
    Math.max(1, constructionNodes.length - 2),
  );

  for (let guard = 0; hasLengthSplitRule && guard < maxIterations; guard += 1) {
    const oversized = intervals
      .map((interval, index) => ({
        interval,
        index,
        length: interval.endDistance - interval.startDistance,
      }))
      .filter((item) => item.length > splitTriggerLength)
      .sort((a, b) => b.length - a.length)[0];

    if (!oversized) {
      break;
    }

    const selected = chooseSplitCandidateForInterval({
      constructionNodes,
      closed,
      pathPositions: pathInfo.positions,
      interval: oversized.interval,
    });

    if (!selected) {
      break;
    }

    splitCandidates.push(selected);

    const left: SplitSearchInterval = {
      startIndex: oversized.interval.startIndex,
      endIndex: selected.constructionIndex,
      startDistance: oversized.interval.startDistance,
      endDistance: selected.position,
    };

    const right: SplitSearchInterval = {
      startIndex: selected.constructionIndex,
      endIndex: oversized.interval.endIndex,
      startDistance: selected.position,
      endDistance: oversized.interval.endDistance,
      closesToLoopStart: oversized.interval.closesToLoopStart,
    };

    intervals.splice(oversized.index, 1, left, right);
  }

  if (splitCandidates.length === 0) {
    return null;
  }

  const splitConstructionIndices = [...new Set(
    splitCandidates.map((candidate) => candidate.constructionIndex),
  )].sort((a, b) => (pathInfo.positions[a] ?? 0) - (pathInfo.positions[b] ?? 0));

  const lineParts = buildLinePartsFromSplitIndices({
    constructionNodes,
    closed,
    splitConstructionIndices,
    pathPositions: pathInfo.positions,
    totalLength,
  });

  if (lineParts.length <= 1) {
    return null;
  }

  const terminalCodesByConstructionIndex = buildTerminalCodesByConstructionIndex({
    splitConstructionIndices,
    lineParts,
    closed,
  });

  const longestCreatedLineLength = Math.max(
    ...lineParts.map((part) => part.length),
  );

  const hasAcuteSplit = splitCandidates.some(isAcuteSourceCandidate);
  const hasCornerLimitSplit = cornerLimitSplitCandidates.length > 0;

  return {
    splitConstructionIndices,
    splitCandidates: splitCandidates.map((candidate): SplitCandidate => ({
      constructionIndex: candidate.constructionIndex,
      position: candidate.position,
      nodeAngle: candidate.nodeAngle,
      isAcuteSource: candidate.isAcuteSource,
      sourcePrimaryIndex: candidate.sourcePrimaryIndex,
    })),
    terminalCodesByConstructionIndex,
    lineParts,
    lineCount: lineParts.length,
    maxSingleLineLength,
    longestCreatedLineLength,
    hasAcuteSplit,
    reason: hasCornerLimitSplit
      ? "max corner count exceeded before length split"
      : hasAcuteSplit
        ? "acute-angle candidate closest to each oversized line half"
        : "normal-angle candidate closest to each oversized line half",
  };
}

function removeSplitHiddenCodes(codes: VertexCode[]): VertexCode[] {
  return codes.filter((code) => {
    /**
     * Split terminal labels take priority.
     * RC and FC are hidden when a vertex becomes the end/start of created lines.
     */
    if (code === "RC" || code === "FC") {
      return false;
    }

    if (code === "T") {
      return false;
    }

    if (/^T\d+$/.test(code)) {
      return false;
    }

    return true;
  });
}

function applySplitDisplayLabels(
  vertices: LifeLineVertex[],
  splitPlan: SplitPlan | null,
): LifeLineVertex[] {
  if (!splitPlan) {
    return vertices;
  }

  return vertices.map((vertex) => {
    const constructionIndex = vertex.constructionIndex;

    if (constructionIndex === undefined) {
      return vertex;
    }

    const terminalCodes =
      splitPlan.terminalCodesByConstructionIndex[constructionIndex];

    if (!terminalCodes) {
      return vertex;
    }

    const displayCodes = [
      ...removeSplitHiddenCodes(vertex.codes),
      ...terminalCodes,
    ];

    return {
      ...vertex,
      displayCodes,
      displayLabel: makeLabel(vertex.sequence, displayCodes),
    };
  });
}

/**
 * Builds once, calculates split, and if a split chooses an acute first-level
 * design vertex, rebuilds with that source vertex forced as a unique vertex.
 */
function buildLifeLineAutoSplit(params: {
  designPoints: Point[];
  closed: boolean;
  systemMaxSpan: number;
  chamferAcuteAngles: boolean;
  chamferSegmentLength: number;
  maxSingleLineLength: number;
  maxCornersPerLifeline: number;
}): {
  build: InternalBuildResult;
  splitPlan: SplitPlan | null;
  forcedPrimaryIndices: number[];
} {
  const {
    designPoints,
    closed,
    systemMaxSpan,
    chamferAcuteAngles,
    chamferSegmentLength,
    maxSingleLineLength,
    maxCornersPerLifeline,
  } = params;

  const forcedPrimaryIndices: number[] = [];

  let build = buildLifeLineOnce({
    designPoints,
    closed,
    systemMaxSpan,
    chamferAcuteAngles,
    chamferSegmentLength,
    forcedPrimaryIndices,
  });

  let splitPlan = findSplitPlan({
    constructionNodes: build.constructionNodes,
    closed,
    totalLength: build.totalLength,
    maxSingleLineLength,
    maxCornersPerLifeline,
  });

  for (let guard = 0; guard < 20; guard += 1) {
    if (!splitPlan) {
      break;
    }

    let changed = false;

    for (const candidate of splitPlan.splitCandidates) {
      const sourcePrimaryIndex = candidate.sourcePrimaryIndex;
      const rawAngle = angleAtFirstLevelVertex(
        designPoints,
        sourcePrimaryIndex,
        closed,
      );

      const isAcuteSource =
        rawAngle !== null &&
        rawAngle < 90 &&
        !isLoopConnectionSource(closed, sourcePrimaryIndex, designPoints.length);

      if (
        isAcuteSource &&
        !forcedPrimaryIndices.includes(sourcePrimaryIndex)
      ) {
        /**
         * Final rule:
         * When an acute vertex is chosen as split terminal,
         * it returns to being one unique first-level vertex with Tn/Tn+1.
         */
        forcedPrimaryIndices.push(sourcePrimaryIndex);
        changed = true;
      }
    }

    if (!changed) {
      break;
    }

    build = buildLifeLineOnce({
      designPoints,
      closed,
      systemMaxSpan,
      chamferAcuteAngles,
      chamferSegmentLength,
      forcedPrimaryIndices,
    });

    splitPlan = findSplitPlan({
      constructionNodes: build.constructionNodes,
      closed,
      totalLength: build.totalLength,
      maxSingleLineLength,
      maxCornersPerLifeline,
    });
  }

  return {
    build,
    splitPlan,
    forcedPrimaryIndices,
  };
}

function makeUnsplitLine(
  constructionNodes: ConstructionNode[],
  closed: boolean,
  totalLength: number,
): SplitLinePart {
  const firstNode = constructionNodes[0];
  return {
    lineNumber: 1,
    startConstructionIndex: 0,
    endConstructionIndex: closed ? 0 : Math.max(0, constructionNodes.length - 1),
    startDistance: 0,
    endDistance: totalLength,
    length: totalLength,
    closesToLoopStart: closed,
    path: closed && firstNode
      ? [...constructionNodes, firstNode]
      : [...constructionNodes],
  };
}

/**
 * Main algorithm.
 */
export function calculateLifeLine(
  inputDesignPoints: Point[],
  inputOptions: LifeLineOptions,
): LifeLineResult {
  const options = normalizeOptions(inputOptions);
  const sanitizedDesignPoints = inputDesignPoints.map(sanitizePoint);

  const closed = options.closed && sanitizedDesignPoints.length > 2;
  const designPoints = rotateClosedLoopToAcuteStart(
    sanitizedDesignPoints,
    closed,
  );

  const autoSplit = buildLifeLineAutoSplit({
    designPoints,
    closed,
    systemMaxSpan: options.systemMaxSpan,
    chamferAcuteAngles: options.chamferAcuteAngles,
    chamferSegmentLength: options.chamferSegmentLength,
    maxSingleLineLength: options.maxSingleLineLength,
    maxCornersPerLifeline: options.maxCornersPerLifeline,
  });

  const vertices = applySplitDisplayLabels(
    autoSplit.build.vertices,
    autoSplit.splitPlan,
  );

  const generatedVertices = vertices.filter((vertex) => vertex.kind === "generated");

  const lines =
    autoSplit.splitPlan?.lineParts ??
    [makeUnsplitLine(
      autoSplit.build.constructionNodes,
      closed,
      autoSplit.build.totalLength,
    )];

  return {
    designPoints,
    closed,
    options: {
      ...options,
      closed,
    },
    constructionNodes: autoSplit.build.constructionNodes,
    vertices,
    generatedVertices,
    spans: autoSplit.build.spans,
    totalLength: autoSplit.build.totalLength,
    splitPlan: autoSplit.splitPlan,
    lines,
  };
}

function getRenderedLifeLineDesignPoints(line: SplitLinePart, result: LifeLineResult): Point[] {
  const designPoints: Point[] = [];
  const sourcePrimaryIndices: number[] = [];

  line.path.forEach((node) => {
    const sourcePrimaryIndex = node.sourcePrimaryIndex;
    const sourcePoint = result.designPoints[sourcePrimaryIndex];

    if (!sourcePoint) {
      return;
    }

    if (sourcePrimaryIndices[sourcePrimaryIndices.length - 1] === sourcePrimaryIndex) {
      return;
    }

    sourcePrimaryIndices.push(sourcePrimaryIndex);
    designPoints.push({ x: sourcePoint.x, y: sourcePoint.y });
  });

  if (
    sourcePrimaryIndices.length > 1 &&
    sourcePrimaryIndices[0] === sourcePrimaryIndices[sourcePrimaryIndices.length - 1]
  ) {
    designPoints.pop();
  }

  return designPoints;
}

export function createRenderedLifeLine(line: SplitLinePart, result: LifeLineResult): RenderedLifeLine {
  const linePoints: Point[] = [];
  const lifeLineVertices: RenderedLifeLineVertex[] = [];

  const addPoint = (point: Point) => {
    const previous = linePoints[linePoints.length - 1];
    if (previous && arePointsEqual(previous, point)) return;
    linePoints.push({ x: point.x, y: point.y });
  };

  const addVertex = (vertex: LifeLineVertex) => {
    const previous = lifeLineVertices[lifeLineVertices.length - 1];
    if (previous && previous.displayLabel === vertex.displayLabel && arePointsEqual(previous, vertex)) return;

    lifeLineVertices.push({
      x: vertex.x,
      y: vertex.y,
      kind: vertex.kind,
      label: vertex.label,
      displayLabel: vertex.displayLabel,
      codes: [...vertex.codes],
      displayCodes: [...vertex.displayCodes],
    });
  };

  const getConstructionVertex = (constructionIndex: number) =>
    result.vertices.find((vertex) => vertex.constructionIndex === constructionIndex);

  line.path.forEach((node, index) => {
    addPoint(node);
    const constructionVertex = getConstructionVertex(node.constructionIndex);
    if (constructionVertex) {
      addVertex(constructionVertex);
    }

    const nextNode = line.path[index + 1];
    if (!nextNode) return;

    result.vertices
      .filter(
        (vertex) =>
          vertex.kind === "generated" &&
          vertex.fromConstructionIndex === node.constructionIndex &&
          vertex.toConstructionIndex === nextNode.constructionIndex,
      )
      .sort((a, b) => (a.localIndex ?? 0) - (b.localIndex ?? 0))
      .forEach((vertex) => {
        addPoint(vertex);
        addVertex(vertex);
      });
  });

  return {
    lineNumber: line.lineNumber,
    line,
    points: linePoints.flatMap((point) => [point.x, point.y]),
    lifeLineVertices,
    designPoints: getRenderedLifeLineDesignPoints(line, result),
  };
}

export function createRenderedLifeLines(result: LifeLineResult): RenderedLifeLine[] {
  return result.lines.map((line) => createRenderedLifeLine(line, result));
}

export function calculateRenderedLifeLines(
  inputDesignPoints: Point[],
  inputOptions: LifeLineOptions,
): { result: LifeLineResult; renderedLifeLines: RenderedLifeLine[] } {
  const result = calculateLifeLine(inputDesignPoints, inputOptions);
  return {
    result,
    renderedLifeLines: createRenderedLifeLines(result),
  };
}

/**
 * Utility: returns only the labels in life-line order.
 */
export function getLifeLineLabels(result: LifeLineResult): string[] {
  return result.vertices.map((vertex) => vertex.displayLabel);
}

/**
 * Utility: returns only vertices that are editable in Grip Mode.
 * These are the original first-level design vertices, not calculated/chamfer/generated vertices.
 */
export function getEditableFirstLevelVertices(designPoints: Point[]): Array<Point & { primaryIndex: number }> {
  return designPoints.map((point, index) => ({
    ...point,
    primaryIndex: index,
  }));
}

/**
 * Example:
 *
 * const result = calculateLifeLine(
 *   [
 *     { x: 100, y: 260 },
 *     { x: 470, y: 260 },
 *     { x: 515, y: 325 },
 *     { x: 350, y: 405 },
 *     { x: 690, y: 405 },
 *   ],
 *   {
 *     closed: false,
 *     systemMaxSpan: 120,
 *     chamferAcuteAngles: true,
 *     chamferSegmentLength: 70,
 *     maxSingleLineLength: 520,
 *   },
 * );
 *
 * console.log(result.vertices.map(v => v.displayLabel));
 * console.log(result.lines.map(line => ({
 *   lineNumber: line.lineNumber,
 *   length: line.length,
 * })));
 */
